import React, { useEffect, useRef, useState } from 'react';
import { company } from '../data/content';
import { CheckIcon, ArrowRightIcon, PhoneIcon, PlayIcon } from './icons';
interface JobVideo {
  id: string;
  label: string;
  blurb: string;
  src: string;
  silent?: boolean;
}
const videos: JobVideo[] = [{
  id: 'cypress',
  label: 'Live in Cypress, TX',
  blurb: 'Our crew on a current job in Cypress.',
  src: 'https://cdn.magicpatterns.com/uploads/5wFyBMcdZjeWqh387Tv77y/85f1903c-226c-41f7-acc3-f9a9845e5d62.MP4'
}, {
  id: 'cypress-2',
  label: 'Cypress, TX · Part 2',
  blurb: 'More from the same Cypress job site.',
  src: 'https://cdn.magicpatterns.com/uploads/1xJh8Z2GnLEY4p8x2TEwqb/216ad7bf-82f1-41cb-aad2-1d5f81175b75.MP4'
}, {
  id: 'crew',
  label: 'On the job',
  blurb: 'Our crew working a pool from prep through finish.',
  src: 'https://cdn.magicpatterns.com/uploads/6D372fRnd4qVVDpSeurpcB/846e8e09-4c30-45c3-b00d-8d1ef499cc8a%20(1).mov'
}, {
  id: 'finished',
  label: 'Finished backyard',
  blurb: 'A completed A&J pool the day we handed it back.',
  src: 'https://cdn.magicpatterns.com/uploads/cM1hVwFjJMVgea9owMWBaC/44e71604-3288-41d9-9cce-2a4b2f41eb3c.MP4',
  silent: true
}];
const points = ['The same crew from estimate to final walkthrough', 'Prep done right before a single bag of plaster opens', 'Job site cleaned up at the end of every day'];
export function VideoSpotlight() {
  const [activeId, setActiveId] = useState(videos[0].id);
  const active = videos.find(video => video.id === activeId) ?? videos[0];
  const videoRef = useRef<HTMLVideoElement>(null);

  // Keep the silent clip muted even if a viewer tries to unmute it.
  useEffect(() => {
    const el = videoRef.current;
    if (!el || !active.silent) return;
    el.muted = true;
    const enforceMute = () => {
      el.muted = true;
    };
    el.addEventListener('volumechange', enforceMute);
    return () => el.removeEventListener('volumechange', enforceMute);
  }, [active]);
  return <section id="video" className="w-full bg-white py-20 lg:py-28" data-id="element-779" data-mp="3cxu6vgjkme5q">
      <div className="mx-auto max-w-content px-5 lg:px-8" data-id="element-780" data-mp="3hvm3uoz2oqgs">
        <div className="grid items-center gap-10 lg:grid-cols-[minmax(0,7fr)_minmax(0,5fr)] lg:gap-14" data-id="element-781" data-mp="3hv459loxu05p">
          <div data-id="element-782" data-mp="334wrvkdsf7bv">
            <div className="relative overflow-hidden rounded-lg bg-navy-900" data-id="element-783" data-mp="3862npr2qvapm">
              <video key={active.src} ref={videoRef} src={`${active.src}#t=0.1`} controls controlsList="nodownload" disablePictureInPicture playsInline muted={Boolean(active.silent)} loop preload="metadata" aria-label={`${active.label} — ${active.blurb}`} className="h-[420px] w-full object-contain sm:h-[520px]" data-id="element-784" data-mp="383znke7vo176">
                Your browser can&apos;t play this video.{' '}
                <a href={active.src} data-id="element-785" data-mp="nfbogiz4xq2j">Download it instead.</a>
              </video>
            </div>

            <div className="mt-4 grid gap-3 sm:grid-cols-2" role="group" aria-label="Choose a video" data-id="element-786" data-mp="27yfh3kaz4jq2">
              {videos.map(video => {
              const isActive = video.id === active.id;
              return <button key={video.id} type="button" onClick={() => setActiveId(video.id)} aria-pressed={isActive} className={`flex items-center gap-3 rounded-md border p-3 text-left transition-colors duration-150 ease-out focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-400 ${isActive ? 'border-navy-800 bg-navy-800' : 'border-navy-200 bg-white hover:border-navy-400'}`} data-id="element-787" data-mp="2cw75w5gbnpq1">
                    <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${isActive ? 'bg-sky-400 text-navy-900' : 'bg-navy-50 text-navy-600'}`} data-id="element-788" data-mp="11bsal5dkrsay">
                      <PlayIcon className="h-4 w-4" data-id="element-789" data-mp="1cpbtp66ewqf6" />
                    </span>
                    <span className="min-w-0" data-id="element-790" data-mp="1hmu3eys979o8">
                      <span className={`block font-display text-[15px] font-bold leading-tight ${isActive ? 'text-white' : 'text-navy-800'}`} data-id="element-791" data-mp="1hlj1z5ma6e0a">
                        {video.label}
                      </span>
                      <span className={`mt-0.5 block text-[13px] leading-snug ${isActive ? 'text-navy-200' : 'text-navy-400'}`} data-id="element-792" data-mp="yp103vdpwxqb">
                        {video.blurb}
                      </span>
                    </span>
                  </button>;
            })}
            </div>
          </div>

          <div data-id="element-793" data-mp="2fho2qua95hb0">
            <p className="text-sm font-semibold text-sky-600" data-id="element-794" data-mp="2fh4ts32ky394">See it in motion</p>
            <h2 className="mt-3 font-display text-3xl font-extrabold leading-tight tracking-tight text-navy-800 sm:text-4xl" data-id="element-795" data-mp="2ai1p4jqihz1o">
              Watch a real A&amp;J job, not a sales pitch.
            </h2>
            <p className="mt-5 text-[15px] leading-relaxed text-navy-500" data-id="element-796" data-mp="20jvr4d7ttd23">
              Photos show the finish. These clips show how it gets there — our crew, our tools, and
              the kind of care we put into a backyard before we hand it back to the family.
            </p>

            <ul className="mt-7 space-y-3" data-id="element-797" data-mp="1mspjftue8944">
              {points.map(point => <li key={point} className="flex items-start gap-3 text-[15px] text-navy-600" data-id="element-798" data-mp="1mqw8w3gnt4qu">
                  <CheckIcon className="mt-1 h-4 w-4 shrink-0 text-sky-600" data-id="element-799" data-mp="1hvqml3jcho28" />
                  {point}
                </li>)}
            </ul>

            <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center" data-id="element-800" data-mp="135w0pu7umn3t">
              <a href="#contact" className="inline-flex items-center justify-center gap-2 rounded-md bg-navy-800 px-6 py-3.5 font-display text-[15px] font-bold text-white transition-colors duration-150 ease-out hover:bg-navy-700" data-id="element-801" data-mp="186abvl4qyd62">
                Get my free estimate
                <ArrowRightIcon className="h-4 w-4" data-id="element-802" data-mp="1lyggqb2g8xq7" />
              </a>
              <a href={company.phoneHref} className="inline-flex items-center justify-center gap-2 text-sm font-semibold text-sky-600 transition-colors duration-150 ease-out hover:text-sky-500" data-id="element-803" data-mp="1qw8g1q5fbm4f">
                <PhoneIcon className="h-4 w-4" data-id="element-804" data-mp="1h55gwtw21h7s" />
                {company.phoneDisplay}
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>;
}